<template>
  <!-- One purchasable plan (1 day / 7 days / 30 days) on the Buy Proxy page. Styled with the
       dashboard's own palette (splash tokens) so the cards sit naturally on the dark page.
       The selected card is the only one with the accent border and the solid purchase button. -->
  <article
    class="package-plan-card"
    :class="{ 'is-selected': selected, 'is-disabled': disabled }"
    role="button"
    :aria-pressed="selected"
    tabindex="0"
    @click="handleSelect"
    @keydown.enter.prevent="handleSelect"
    @keydown.space.prevent="handleSelect"
  >
    <span v-if="selected" class="package-plan-card__badge">{{ t('plan.selected') }}</span>

    <div class="package-plan-card__top">
      <p class="package-plan-card__period">{{ periodLabel }}</p>
      <span v-if="savingText" class="package-plan-card__saving">{{ savingText }}</span>
    </div>
    <p class="package-plan-card__price">${{ priceText }}</p>
    <p class="package-plan-card__perday">{{ perDayText }}</p>

    <ul class="package-plan-card__features">
      <li><v-icon icon="mdi-database-outline" size="18"></v-icon><span>{{ dataText }}</span></li>
      <li><v-icon icon="mdi-refresh" size="18"></v-icon><span>{{ rotationText }}</span></li>
      <li v-if="plan?.max_speed_mbps" class="package-plan-card__speed"><v-icon icon="mdi-speedometer" size="18"></v-icon><span>{{ t('plan.speed', { n: plan.max_speed_mbps }) }}</span></li>
      <li><v-icon icon="mdi-shield-check-outline" size="18"></v-icon><span>{{ t('plan.httpSocks') }}</span></li>
      <li v-if="offers('openvpn')"><img :src="openVpnLogo" class="package-plan-card__vpn" alt="" /><span>{{ t('plan.openvpn') }}</span></li>
      <li v-if="offers('wireguard')"><img :src="wireGuardLogo" class="package-plan-card__vpn package-plan-card__vpn--wg" alt="" /><span>{{ t('plan.wireguard') }}</span></li>
      <li v-if="offers('os_spoofing')"><v-icon icon="mdi-monitor-cellphone" size="18"></v-icon><span>{{ t('plan.osSpoofing') }}</span></li>
      <li v-if="offers('udp')"><v-icon icon="mdi-swap-horizontal" size="18"></v-icon><span>{{ t('plan.udp') }}</span></li>
    </ul>

    <button
      type="button"
      class="package-plan-card__action"
      :class="{ 'is-selected': selected }"
      :disabled="disabled"
      @click.stop="handleAction"
    >
      {{ buttonText }}
    </button>
  </article>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";
import { t } from "@/i18n";
import openVpnLogo from "@/assets/openVPN.png";
import wireGuardLogo from "@/assets/wireguard.svg";

const props = defineProps({
  plan: { type: Object, required: true },
  period: { type: String, required: true },            // 'daily' | 'weekly' | 'monthly'
  selectedDetails: { type: Object, required: true },   // kept for compatibility (location is shown above the grid now)
  selected: { type: Boolean, default: false },
  actionLabel: { type: String, default: "Select" },
  selectedActionLabel: { type: String, default: "Purchase" },
  disabled: { type: Boolean, default: false },
  dailyPrice: { type: Number, default: 0 },            // price of the 1-day plan, for the saving note
});

const emit = defineEmits(["select", "action"]);

const DAYS = { daily: 1, weekly: 7, monthly: 30 };

const handleSelect = () => { if (!props.disabled) emit("select"); };
const handleAction = () => { if (props.disabled) return; handleSelect(); emit("action"); };

const price = computed(() => Number(props.plan?.price) || 0);
// plan.features lists what the plan's server offers (its type + the admin's Features switches on the server).
// A plan without the list (an older response) keeps the old rule: ProxySmart = OpenVPN.
const offers = (name) => Array.isArray(props.plan?.features) ? props.plan.features.includes(name) : (name === 'openvpn' && isProxySmart.value);
const isProxySmart = computed(() => String(props.plan?.type || "").toUpperCase() === "PROXYSMART");   // ProxySmart servers offer OpenVPN
const days = computed(() => DAYS[props.period] || 1);
const money = (v) => (Math.round(v * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

const periodLabel = computed(() => ({ daily: t('plan.daily'), weekly: t('plan.weekly'), monthly: t('plan.monthly') })[props.period] || props.period);
const priceText = computed(() => money(price.value));
const perDayText = computed(() => days.value > 1 ? t('plan.perDayPrice', { price: (price.value / days.value).toFixed(2) }) : t('plan.perDay'));
// saving against buying the same days one at a time
const savingText = computed(() => {
  if (!props.dailyPrice || days.value <= 1) return "";
  const full = props.dailyPrice * days.value;
  const pct = Math.round((1 - price.value / full) * 100);
  return pct >= 5 ? t('plan.save', { pct }) : "";
});
const dataText = computed(() => props.plan?.data_limit ? t('plan.gbPerDay', { gb: props.plan.data_limit }) : t('plan.unlimited'));
// the enforced cooldown between IP changes is the package's min_time_change_ip (minutes); rotation_time is
// only an older display field, used here as a fallback
const rotationText = computed(() => {
  const mins = Number(props.plan?.min_time_change_ip) || Number(props.plan?.rotation_time) || 0;
  return mins > 0 ? t('plan.rotation', { mins }) : t('plan.rotationOnDemand');
});
const buttonText = computed(() => {
  if (props.disabled) return t('plan.outOfStock');
  return props.selected ? t('plan.actionFor', { action: props.selectedActionLabel, price: priceText.value }) : props.actionLabel;
});
</script>

<style scoped>
.package-plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 16px 16px 14px;
  border: 1px solid var(--ui-line);
  border-radius: 12px;
  background: var(--ui-card);
  color: var(--ui-ink);
  text-align: left;
  cursor: pointer;
  outline: none;
  transition: border-color 150ms ease, transform 150ms ease;
}
.package-plan-card:hover,
.package-plan-card:focus-visible { border-color: var(--ui-pri-hover); transform: translateY(-1px); }
.package-plan-card.is-selected { border: 2px solid var(--ui-pri); padding: 15px 15px 13px; }
.package-plan-card.is-disabled { cursor: not-allowed; }
.package-plan-card.is-disabled:hover { transform: none; border-color: var(--ui-line); }

.package-plan-card__badge {
  position: absolute; top: -10px; left: 14px;
  padding: 2px 10px; border-radius: 999px;
  background: var(--ui-pri); color: var(--ui-pri-ink);
  font-size: 12px; font-weight: 600; line-height: 16px;
}
.package-plan-card__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.package-plan-card__period { margin: 0; font-size: 13px; letter-spacing: 0.04em; text-transform: uppercase; color: var(--ui-dim); }
.package-plan-card__saving { font-size: 12px; color: var(--ui-teal); white-space: nowrap; }
.package-plan-card__price { margin: 6px 0 0; font-size: 30px; line-height: 1.1; font-weight: 600; color: var(--ui-ink); }
.package-plan-card__perday { margin: 0 0 12px; font-size: 13px; color: var(--ui-dim); }

.package-plan-card__features {
  list-style: none; margin: 0; padding: 10px 0 0;
  border-top: 1px solid var(--ui-line);
  display: grid; gap: 6px;
  font-size: 13.5px; color: var(--ui-dim);
  flex: 1;
}
.package-plan-card__features li { display: flex; align-items: center; gap: 8px; line-height: 1.35; }
.package-plan-card__features .v-icon { color: var(--ui-teal); }
.package-plan-card__vpn { height: 18px; width: 18px; object-fit: contain; }
/* a capped server: amber, so it is not missed among the grey lines */
.package-plan-card__speed, .package-plan-card__speed .v-icon { color: #f59e0b !important; font-weight: 700; }
/* the WireGuard mark is dark red: a white disc keeps it readable on the dark and the light card */
.package-plan-card__vpn--wg { background: #ffffff; border-radius: 50%; padding: 1px; }

.package-plan-card__action {
  margin-top: 14px; width: 100%; min-height: 36px;
  border: 1px solid var(--ui-line); border-radius: 8px;
  background: transparent; color: var(--ui-ink);
  font-size: 14px; font-weight: 500; padding: 9px 12px; text-align: center;
  transition: background-color 150ms ease, border-color 150ms ease;
}
.package-plan-card__action:hover { border-color: var(--ui-pri-hover); }
.package-plan-card__action.is-selected {
  border-color: var(--ui-pri);
  background: var(--ui-pri); color: var(--ui-pri-ink); font-weight: 600;
}
.package-plan-card__action.is-selected:hover { background: var(--ui-pri-hover); }
.package-plan-card__action:disabled { cursor: not-allowed; color: var(--ui-dim); }
</style>
